import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import imageHeaderBanner from "./../../../assets/images/domain/house/house.jpg";

import H1 from "../../../components/Titles/h1";
import Button from "../../../components/Buttons/button";
import DescriptionSplit from "../../../components/Description/description-split";
import BackgroundImage from "../../../components/Banner/background-header";
import RoomDetails from "../components/room-details";
import { Helmet } from "react-helmet";

import imageData from './../../../utils/imports/import-images.json';

const listOfRooms = ['livingRoom', 'kitchen', 'diningRoom', 'smallLivingRoom'];
const listOfSuits = ['nacreSuit', 'topazeSuit', 'onyxSuit', 'amazoniteSuit', 'seleniteSuit'];

export default function HouseDetails() {
    const { t } = useTranslation();

    const scrollTargetRooms = useRef(null);
    const scrollTargetSuits = useRef(null);
    const scrollTargetOutdoors = useRef(null);

    const [headerHeight, setHeaderHeight] = useState(0);

    const extractImages = (list, category) => {
        return list.reduce((acc, item) => {
            if (imageData['house'][category][item]) {
                acc[item] = imageData['house'][category][item];
            }
            return acc;
        }, {});
    };

    const roomImages = extractImages(listOfRooms, 'rooms');
    const suitImages = extractImages(listOfSuits, 'suits');
    const outdoorImages = imageData?.house?.outdoors || [];

    useEffect(() => {
        const updateHeaderHeight = () => {
            const header = document.querySelector('header');
            if (header) setHeaderHeight(header.offsetHeight);
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);
        return () => window.removeEventListener('resize', updateHeaderHeight);
    }, []);

    const handleScrollToDiv = (ref) => {
        if (ref.current) {
            const top = ref.current.getBoundingClientRect().top + window.scrollY - headerHeight - 10;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <div>
            <Helmet>
                <title>{t('houseDetails.page')}</title>
                <meta name="description" content={t('houseDetails.description')} />
                <meta name="keywords" content={t('houseDetails.keywords')} />
            </Helmet>

            <div className="absolute top-0 w-full">
                <BackgroundImage image={imageHeaderBanner} alt={t('reservation.altBackground')} />
            </div>

            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-12 md:bottom-24 w-4/5 md:w-3/5 text-center">
                <H1 title={t('houseDetails.title')} />

                <div className="mt-4 mb-6">
                    <DescriptionSplit description={t('houseDetails.titleDescription')} text='center' />
                </div>

                <div className="flex flex-col md:flex-row flex-wrap items-center justify-center gap-4 md:gap-10">
                    <Button text={t('houseDetails.buttonLivingRooms')} onClick={() => handleScrollToDiv(scrollTargetRooms)} />
                    <Button text={t('houseDetails.buttonSuits')} onClick={() => handleScrollToDiv(scrollTargetSuits)} />
                    <Button text={t('houseDetails.buttonOutdoors')} onClick={() => handleScrollToDiv(scrollTargetOutdoors)} />
                </div>
            </div>

            <div className="max-w-[2000px] m-auto">
                <div ref={scrollTargetRooms} className="relative mt-[100vh]">
                    {listOfRooms.map((room, index) => (
                        <RoomDetails
                            key={room}
                            data={t(`houseDetails.${room}`, { returnObjects: true })}
                            imagesPaths={roomImages[room] || []}
                            carouselOn={index % 2 === 0 ? 'left' : 'right'}
                        />
                    ))}
                </div>

                <div ref={scrollTargetSuits}>
                    <div className="my-8 md:my-12 bg-secondary py-4 md:py-6 px-12 md:px-24 w-fit m-auto">
                        <H1 title={t("houseDetails.suits")} fontWeight="semibold" theme="white" />
                    </div>

                    {listOfSuits.map((suit, index) => (
                        <RoomDetails
                            key={suit}
                            data={t(`houseDetails.${suit}`, { returnObjects: true })}
                            imagesPaths={suitImages[suit] || []}
                            carouselOn={index % 2 === 0 ? 'left' : 'right'}
                        />
                    ))}
                </div>

                <div ref={scrollTargetOutdoors}>
                    <div className="my-8 md:my-12 bg-secondary py-4 md:py-6 px-12 md:px-24 w-fit m-auto">
                        <H1 title={t("houseDetails.outdoors.title")} fontWeight="semibold" theme="white" />
                    </div>

                    <RoomDetails
                        data={t('houseDetails.outdoors', { returnObjects: true })}
                        imagesPaths={outdoorImages}
                        carouselOn="left"
                    />
                </div>
            </div>
        </div>
    );
}
